import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import BasketServices from "../../components/basket/basketService/BasketService";
import { IProductDetail } from "../../components/product-detail-component/type/Type";

export const fetchBasketData = createAsyncThunk(
  "basket/fetchBasketData",
  async (_, { rejectWithValue }) => {
    try {
      const res = await BasketServices.AllBasket();
      return res;
    } catch (err) {
      return rejectWithValue("Failed to fetch basket data");
    }
  }
);

export interface BasketItem {
  id: number;
  basket_id?: number;
  product?: IProductDetail;
  product_id: number;
  quantity: number;
  min_order_quantity?: number;
  discount_price?: number | null;
  price: number | string;
  cover_image?: string;
  variant_image?: string;
  name?: string;
  slug?: string;
  variant_id?: number;
  attributes?: string[] | any;
  model_code?: string | null;
  currency?: string;
}

interface BasketState {
  basketItems: BasketItem[];
  totalQuantity: number;
  totalPrice: number;
  totalDiscountPrice: number;
  discountedPrice: number;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  isBasketOpen: boolean;
}

const initialState: BasketState = {
  basketItems: [],
  totalQuantity: 0,
  totalPrice: 0,
  totalDiscountPrice: 0,
  discountedPrice: 0,
  status: "idle",
  error: null,
  isBasketOpen: false,
};

const basketSlice = createSlice({
  name: "basket",
  initialState,
  reducers: {
    clearBasket(state) {
      state.basketItems = [];
      state.totalQuantity = 0;
      state.totalPrice = 0;
      state.totalDiscountPrice = 0;
      state.discountedPrice = 0;
    },
    addItem(state, action) {
      const newItem = action.payload;
      const existingItem = state.basketItems.find(
        (item) => item.id === newItem.id
      );
      if (existingItem) {
        existingItem.quantity += newItem.quantity;
      } else {
        state.basketItems.push(newItem);
      }
      state.totalQuantity = state.basketItems.reduce(
        (total, item) => total + item.quantity,
        0
      );

      state.totalPrice = state.basketItems.reduce(
        (total, item) =>
          total + (Number(item.discount_price || item.price) || 0) * item.quantity,
        0
      );
    },

    incrementQuantity(state, action) {
      const item = state.basketItems.find((item) => item.id === action.payload);
      if (item) {
        item.quantity += 1;
      }
      state.totalQuantity = state.basketItems.reduce(
        (total, item) => total + item.quantity,
        0
      );
    },

    decrementQuantity(state, action) {
      const item = state.basketItems.find((item) => item.id === action.payload);
      const minQty = Math.max(1, Number(item?.min_order_quantity) || 1);
      if (item && item.quantity > minQty) {
        item.quantity -= 1;
      }
      state.totalQuantity = state.basketItems.reduce(
        (total, item) => total + item.quantity,
        0
      );
    },

    removeItem(state, action) {
      state.basketItems = state.basketItems.filter(
        (item) => item.id !== action.payload && item.basket_id !== action.payload
      );
      state.totalQuantity = state.basketItems.reduce(
        (total, item) => total + item.quantity,
        0
      );
    },
    setIsBasketOpen(state, action: PayloadAction<boolean>) {
      state.isBasketOpen = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchBasketData.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchBasketData.fulfilled, (state, action) => {
        state.status = "succeeded";
        const payload: any = action.payload;

        const rawItems: any[] = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
          ? payload.data
          : [];

        const items: BasketItem[] = rawItems.map((item) => ({
          ...item,
          id: item.basket_id ?? item.id ?? item.product_id,
        }));

        state.basketItems = items;

        state.totalQuantity = items.reduce(
          (total: number, item: BasketItem) =>
            total + (Number(item?.quantity) || 1),
          0
        );

        // Take prices directly from /api/basket response itself!
        const beTotalPrice =
          payload && payload.total_price !== undefined && payload.total_price !== null
            ? Number(payload.total_price)
            : null;
        const beTotalDiscountPrice =
          payload &&
          payload.total_discount_price !== undefined &&
          payload.total_discount_price !== null
            ? Number(payload.total_discount_price)
            : null;

        if (beTotalPrice !== null && !isNaN(beTotalPrice)) {
          state.totalPrice = beTotalPrice;
        } else {
          state.totalPrice = items.reduce(
            (total: number, item: BasketItem) =>
              total + (Number(item.discount_price || item.price) || 0) * (Number(item.quantity) || 1),
            0
          );
        }

        if (beTotalDiscountPrice !== null && !isNaN(beTotalDiscountPrice)) {
          state.totalDiscountPrice = beTotalDiscountPrice;
        } else {
          state.totalDiscountPrice = state.totalPrice;
        }

        const beDiscountedPrice =
          payload && payload.discounted_price !== undefined && payload.discounted_price !== null
            ? Number(payload.discounted_price)
            : null;

        const itemDiscounts = items.reduce((acc: number, item: BasketItem) => {
          if (item.discount_price && Number(item.price) > Number(item.discount_price)) {
            return acc + (Number(item.price) - Number(item.discount_price)) * (Number(item.quantity) || 1);
          }
          return acc;
        }, 0);

        if (beDiscountedPrice !== null && !isNaN(beDiscountedPrice) && beDiscountedPrice > 0) {
          state.discountedPrice = beDiscountedPrice;
        } else if (state.totalPrice > state.totalDiscountPrice) {
          state.discountedPrice = Math.max(0, state.totalPrice - state.totalDiscountPrice);
        } else if (itemDiscounts > 0) {
          state.discountedPrice = itemDiscounts;
        } else {
          state.discountedPrice = 0;
        }
      })
      .addCase(fetchBasketData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      });
  },
});

export const {
  clearBasket,
  addItem,
  incrementQuantity,
  decrementQuantity,
  removeItem,
  setIsBasketOpen,
} = basketSlice.actions;
export default basketSlice.reducer;
